import { ViewBox } from '../../types';
import { getViewBoxAfterZoom } from '../MouseEvents/helpers';

/** The relevant, serialisable slice of a wheel event. */
export interface WheelInput {
  deltaX: number;
  deltaY: number;
  offsetX: number;
  offsetY: number;
}

/**
 * A fractional deltaY is the signature of a pinch/scroll gesture on a trackpad,
 * which we treat as a zoom even without the meta modifier held.
 */
export const isZoomingTrackpad = (deltaY: number): boolean => deltaY % 1 !== 0;

/**
 * Pure computation of the next view box for a wheel gesture, or `null` when the
 * gesture should be ignored (a zoom while already at a zoom limit). Zoom happens
 * when meta is held or the gesture comes from a trackpad; otherwise the wheel
 * pans the canvas.
 */
export const computeWheelViewBox = (
  input: WheelInput,
  viewBox: ViewBox,
  isMetaPressed: boolean
): ViewBox | null => {
  const { deltaX, deltaY, offsetX, offsetY } = input;

  if (isMetaPressed || isZoomingTrackpad(deltaY)) {
    const newViewBox = getViewBoxAfterZoom(viewBox, offsetX, offsetY, deltaY);
    if (newViewBox.w === viewBox.w) {
      // Already at a zoom limit; nothing to apply.
      return null;
    }
    return { ...viewBox, ...newViewBox };
  }

  return {
    ...viewBox,
    x: viewBox.x + deltaX,
    y: viewBox.y + deltaY,
  };
};
